import type { OutreachFunnelSteps } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { CrmCard, CrmCardNote } from './CrmCard';
import { funnelBars, funnelSummary, hasFunnelCandidates, type FunnelBarModel } from './crmOverviewModel';

const UNKNOWN = '—';
const CONTRAST_TEXT: Record<FunnelBarModel['contrast'], string> = {
  'on-light': 'text-foreground',
  'on-dark': 'text-white',
};
export const FUNNEL_TITLE = 'Funnel';
export const FUNNEL_UNAVAILABLE = 'The funnel shows here once the server is updated.';
export const FUNNEL_EMPTY = 'No outreach activity recorded yet. The funnel fills in as creators are added and contacted.';

/** Figma's tapered funnel: each bar is centred in its track so it narrows on both sides, with its share of candidates inside. */
export function CrmFunnelCard({ steps }: { steps: OutreachFunnelSteps | null | undefined }) {
  return (
    <CrmCard title={FUNNEL_TITLE}>
      <FunnelBody steps={steps} />
    </CrmCard>
  );
}

/** Every step is a share of candidates, so with none there is nothing to draw but empty stubs. */
function FunnelBody({ steps }: { steps: OutreachFunnelSteps | null | undefined }) {
  if (!steps) return <CrmCardNote>{FUNNEL_UNAVAILABLE}</CrmCardNote>;
  if (!hasFunnelCandidates(steps)) return <CrmCardNote>{FUNNEL_EMPTY}</CrmCardNote>;
  return <FunnelChart steps={steps} />;
}

function FunnelChart({ steps }: { steps: OutreachFunnelSteps }) {
  const bars = funnelBars(steps);
  return (
    <div role="img" aria-label={funnelSummary(bars)} className="space-y-2">
      {bars.map((bar) => (
        <FunnelRow key={bar.key} bar={bar} />
      ))}
    </div>
  );
}

function FunnelRow({ bar }: { bar: FunnelBarModel }) {
  return (
    <div aria-hidden className="flex items-center gap-3 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{bar.label}</span>
      <div className="flex h-8 flex-1 items-center justify-center">
        <FunnelFill bar={bar} />
      </div>
      <span className="w-8 shrink-0 text-right tabular-nums text-foreground">{bar.count ?? UNKNOWN}</span>
    </div>
  );
}

/** An unmeasured step (Signed until the close is built) draws no bar, only its dash. */
function FunnelFill({ bar }: { bar: FunnelBarModel }) {
  if (bar.count === null) return <span className="text-[12px] text-muted-foreground">{UNKNOWN}</span>;
  return (
    <div
      className={cn('flex h-8 min-w-16 items-center justify-center rounded-md px-2', CONTRAST_TEXT[bar.contrast])}
      style={{ width: `${bar.widthPct}%`, background: bar.fill }}
    >
      <span className="text-[12px] font-medium">{bar.share}</span>
    </div>
  );
}
